import { Fragment, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { DropdownMenu, DropdownMenuItem } from '@/components/ui/DropdownMenu';
import { formatDuracionCorta } from '@/utils/formatDuration';
import { Paperclip, History, Pencil, ExternalLink, Lock, MoreVertical, Trash2, XCircle } from 'lucide-react';
import { useAdminSecretPanel } from '@/hooks/useAdminSecretPanel';
import {
  actorDeEntry,
  dedupeHistorialRuido,
  estadoConversacion,
  type Actor,
  type EstadoConversacion,
  type RevisionEntry,
} from '@/utils/revisionHistorial';

export interface CorreccionesChatProps {
  /** Una sola vez filtrado por elemento; el componente ordena y deduplica internamente. */
  entries: RevisionEntry[];
  /** Perspectiva usada para alinear las burbujas propias vs. ajenas. */
  actorActual: 'asesor' | 'admin';
  /** Permiso, calculado por el llamador, para mostrar el cuadro de respuesta. */
  canReply: boolean;
  onReply?: (texto: string) => Promise<void>;
  /** Permite corregir el texto de la ÚLTIMA respuesta propia, mientras siga siendo el turno
   * del actor actual (esperando al otro lado) — no crea un mensaje nuevo, edita el existente. */
  onEditReply?: (texto: string) => Promise<void>;
  /** Elimina el reemplazo vigente (restaura el elemento rechazado). Se ofrece junto al tag del
   * archivo marcado como `eliminable`, solo visible para el asesor. */
  onEliminarReemplazo?: () => Promise<void>;
  /** Comentarios que le quedan al asesor en la ronda vigente (para mostrar el contador y topar).
   * Solo aplica a la perspectiva del asesor; el admin ya no comenta en el chat. */
  comentariosRestantes?: number;
  emptyLabel?: string;
  className?: string;
  permiteSubirEvidenciasNuevas?: boolean | null;
}

const COPY_SISTEMA: Record<string, string> = {
  SISTEMA_EVIDENCIA_SUBIDA: 'Se subió una nueva versión del archivo.',
  SISTEMA_AUDIO_VINCULADO: 'Se vinculó un nuevo audio en reemplazo del rechazado.',
  SISTEMA_REEMPLAZO_ELIMINADO: 'Se eliminó el archivo de reemplazo.',
  SISTEMA_EVIDENCIA_ELIMINADA: 'Se eliminó la evidencia.',
  SISTEMA_AUDIO_ELIMINADO: 'Se eliminó el audio.',
  SISTEMA_REVISION_INICIALIZADA: 'Se inicializó el estado de revisión de la asesoría.',
  SISTEMA_ARCHIVO_ORIGINAL: 'Archivo original subido por el asesor.',
};

const COPY_ESTADO_CONVERSACION: Record<EstadoConversacion, string> = {
  APROBADO: 'Aprobado',
  RECHAZADO: 'Rechazado',
  // No decimos "esperando revisión del administrador": responder a este elemento no implica que
  // la asesoría completa ya se haya reenviado a revisión — eso podría seguir sin hacerse.
  ESPERANDO_ADMIN: 'Respuesta enviada',
  ESPERANDO_ASESOR: 'Esperando respuesta del asesor',
  CORREGIDO: 'Corrección enviada',
  SIN_ACTIVIDAD: 'Sin actividad',
};

const CHIP_ESTADO_CONVERSACION: Record<EstadoConversacion, string> = {
  APROBADO: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
  RECHAZADO: 'bg-red-100 text-red-800 border border-red-300',
  ESPERANDO_ADMIN: 'bg-amber-50 text-amber-700 border border-amber-300',
  // "Esperando respuesta del asesor" es el estado que se ve tras un rechazo recién hecho:
  // mismo tratamiento de fill que "Aprobado", en rojo.
  ESPERANDO_ASESOR: 'bg-red-100 text-red-800 border border-red-300',
  CORREGIDO: 'bg-blue-50 text-blue-700 border border-blue-300',
  SIN_ACTIVIDAD: 'bg-gray-50 text-gray-500 border border-gray-200',
};

const COPY_ETIQUETA_ADMIN: Record<string, string> = {
  APROBADO: 'Aprobó',
  RECHAZADO: 'Rechazó',
  COMENTADO: 'Comentó',
  DEVUELTA: 'Devolvió',
};

function copyPorEstadoSistema(estado: string, observacion?: string | null): string {
  return COPY_SISTEMA[estado] ?? observacion ?? 'Evento del sistema';
}

/**
 * Label del chip de estado, ajustado por perspectiva. Un rechazo individual (evidencia/audio/campo)
 * puede ocurrir mientras el admin todavía está revisando ítem por ítem, ANTES de devolver la
 * asesoría completa al asesor (acción aparte, "Devolver Asesoría"). Hasta que eso ocurra, el asesor
 * no se ha enterado del rechazo y no puede responder — decirle al admin "Esperando respuesta del
 * asesor" es engañoso, así que ve "Rechazado" en su lugar.
 *
 * Desde la vista del asesor, "Esperando respuesta del asesor" también es engañoso pero por otra
 * razón: le habla de sí mismo en tercera persona, como si otra persona describiera su turno. Se le
 * muestra en segunda persona en su lugar, dirigido a él.
 */
function labelChipEstadoConversacion(estado: EstadoConversacion, actorActual: 'asesor' | 'admin'): string {
  if (estado !== 'ESPERANDO_ASESOR') return COPY_ESTADO_CONVERSACION[estado];
  return actorActual === 'admin' ? 'Rechazado' : 'Debes responder';
}

/** Fecha y hora del mensaje en hora de Colombia (America/Bogota), independiente de la zona
 * horaria del navegador de quien lo lee. */
function formatFechaColombia(fecha: string): string {
  return new Date(fecha).toLocaleString('es-CO', {
    timeZone: 'America/Bogota',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function byFechaAsc(a: RevisionEntry, b: RevisionEntry): number {
  return new Date(a.fecha).getTime() - new Date(b.fecha).getTime();
}

function MessageBubble({
  entry,
  actorActual,
  editable,
  onEditar,
  onEliminarReemplazo,
  showSecretPanel,
}: {
  entry: RevisionEntry;
  actorActual: 'asesor' | 'admin';
  editable?: boolean;
  onEditar?: () => void;
  onEliminarReemplazo?: () => Promise<void>;
  showSecretPanel?: boolean;
}) {
  const actor: Actor = actorDeEntry(entry);
  const esPropio = actor === actorActual;
  const [eliminando, setEliminando] = useState(false);

  const handleEliminar = async () => {
    if (!onEliminarReemplazo) return;
    if (!confirm('¿Eliminar este reemplazo? Esto restaurará el elemento rechazado.')) return;
    setEliminando(true);
    try {
      await onEliminarReemplazo();
    } finally {
      setEliminando(false);
    }
  };

  // "Corrección enviada." (CORREGIDO sin texto) es un marcador automático del sistema, no un
  // comentario que el asesor escribió — se dibuja como evento neutral para no confundirlo con un
  // comentario (no consume el cupo de la ronda).
  if (entry.estado === 'CORREGIDO' && !entry.observacion) {
    return (
      <div className="cc-bubble cc-bubble--sistema flex justify-center my-2" data-testid="cc-bubble" data-actor="sistema">
        <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-3 py-1 italic">
          Corrección enviada.
          <span className="ml-1.5 text-[10px] text-gray-400 not-italic" data-testid="cc-bubble-fecha">
            {formatFechaColombia(entry.fecha)}
          </span>
        </span>
      </div>
    );
  }

  if (actor === 'sistema') {
    const archivo = entry.archivoAdjunto;
    return (
      <div className="cc-bubble cc-bubble--sistema flex flex-col items-center my-2 gap-1" data-testid="cc-bubble" data-actor={actor}>
        <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-3 py-1">
          {copyPorEstadoSistema(entry.estado, entry.observacion)}
          <span className="ml-1.5 text-[10px] text-gray-400" data-testid="cc-bubble-fecha">
            {formatFechaColombia(entry.fecha)}
          </span>
        </span>
        {archivo && (
          <div
            data-testid={archivo.url ? 'cc-archivo-adjunto' : 'cc-archivo-adjunto-tag'}
            title={archivo.url ? undefined : 'Versión ya no accesible'}
            className={`flex flex-col gap-0.5 max-w-[85%] rounded-lg border px-2.5 py-1.5 ${
              archivo.url ? 'text-blue-700 bg-blue-50 border-blue-100' : 'text-gray-500 bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex items-center gap-1">
              {archivo.url ? <Paperclip className="w-3 h-3 shrink-0" /> : <Lock className="w-3 h-3 shrink-0" />}
              <span className="text-xs font-medium truncate">{archivo.nombre}</span>
              {archivo.duracionSeg != null && (
                <span className="text-xs shrink-0 opacity-70">· {formatDuracionCorta(archivo.duracionSeg)}</span>
              )}
              {(archivo.url || (archivo.eliminable && onEliminarReemplazo)) && (
                <DropdownMenu
                  label="Opciones del archivo"
                  buttonClassName="p-0.5 rounded hover:bg-black/5 shrink-0 ml-auto"
                  trigger={<MoreVertical className="w-3.5 h-3.5" />}
                >
                  {archivo.url && (
                    <DropdownMenuItem
                      icon={<ExternalLink className="w-3.5 h-3.5" />}
                      onClick={() => window.open(archivo.url!, '_blank', 'noopener,noreferrer')}
                    >
                      <span data-testid="cc-archivo-abrir">Abrir</span>
                    </DropdownMenuItem>
                  )}
                  {archivo.eliminable && onEliminarReemplazo && (
                    <DropdownMenuItem
                      icon={<Trash2 className="w-3.5 h-3.5" />}
                      variant="danger"
                      disabled={eliminando}
                      onClick={handleEliminar}
                    >
                      <span data-testid="cc-eliminar-reemplazo">{eliminando ? 'Eliminando...' : 'Eliminar'}</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenu>
              )}
            </div>
            {archivo.descripcion && (
              <p data-testid="cc-archivo-descripcion" className="text-[11px] italic opacity-80 wrap-break-word whitespace-pre-wrap">
                "{archivo.descripcion}"
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  const alineacion = esPropio ? 'justify-end' : 'justify-start';
  const colorActor: Record<Exclude<Actor, 'sistema'>, string> = {
    admin: 'bg-blue-50 text-blue-900 border border-blue-100',
    asesor: 'bg-amber-50 text-amber-900 border border-amber-100',
  };

  return (
    <div className={`cc-bubble flex ${alineacion} my-1`} data-testid="cc-bubble" data-actor={actor}>
      <div className={`rounded-lg px-3 py-2 max-w-[75%] ${colorActor[actor as Exclude<Actor, 'sistema'>]}`}>
        <div className="text-xs font-medium mb-0.5 opacity-70">
          {actor === 'admin' ? (entry.adminNombre ?? 'Administrador') : 'Asesor'}
          {COPY_ETIQUETA_ADMIN[entry.estado] ? ` · ${COPY_ETIQUETA_ADMIN[entry.estado]}` : ''}
          {entry.version != null && (
            <span data-testid="cc-comentario-version" className="opacity-70">
              {' '}
              · Revisión #{entry.version}
            </span>
          )}
          {showSecretPanel && (
            <span className="font-mono text-[9px] text-amber-800 bg-amber-100 px-1 py-0.5 rounded border border-amber-200 select-all font-semibold ml-1.5">
              #{entry.historialId ?? entry.id} [{entry.estado}]
            </span>
          )}
        </div>
        {entry.estado === 'CORREGIDO' && !entry.observacion ? (
          <span className="text-sm italic">Corrección enviada.</span>
        ) : (
          <span className="text-sm whitespace-pre-wrap">{entry.observacion}</span>
        )}
        <div className="flex items-center justify-end gap-1.5 mt-0.5">
          {editable && (
            <button
              type="button"
              onClick={onEditar}
              data-testid="cc-editar-respuesta"
              className="inline-flex items-center gap-0.5 text-[10px] opacity-60 hover:opacity-100 underline"
            >
              <Pencil className="w-2.5 h-2.5" /> Editar
            </button>
          )}
          <div className="text-[10px] opacity-60" data-testid="cc-bubble-fecha">
            {formatFechaColombia(entry.fecha)}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CorreccionesChat({
  entries,
  actorActual,
  canReply,
  onReply,
  onEditReply,
  onEliminarReemplazo,
  comentariosRestantes,
  emptyLabel = 'Aún no hay actividad en este elemento.',
  className = '',
  permiteSubirEvidenciasNuevas,
}: CorreccionesChatProps) {
  const showSecretPanel = useAdminSecretPanel();
  const orden = dedupeHistorialRuido([...entries].sort(byFechaAsc));
  const estado = estadoConversacion(entries);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [orden.length]);

  // El asesor puede escribir varios comentarios por ronda (el tope lo aplica el llamador vía
  // `canReply` + `comentariosRestantes`); igual puede corregir su ÚLTIMO mensaje en el sitio
  // mientras siga esperando al admin, en vez de gastar otro comentario.
  const ultimoEntry = orden[orden.length - 1];
  const puedeEditarUltimo = !!onEditReply && !!ultimoEntry && actorDeEntry(ultimoEntry) === actorActual && estado === 'ESPERANDO_ADMIN';

  const modoEdicion = editandoId != null;
  const inputDisabled = enviando;
  const mostrarContador = comentariosRestantes != null;
  const contadorMensaje =
    comentariosRestantes != null && comentariosRestantes > 0
      ? `Te queda${comentariosRestantes === 1 ? '' : 'n'} ${comentariosRestantes} comentario${comentariosRestantes === 1 ? '' : 's'} en esta revisión.`
      : permiteSubirEvidenciasNuevas === false
        ? 'Has alcanzado el máximo de comentarios en esta revisión.'
        : 'Has alcanzado el máximo de comentarios en esta revisión. Puedes seguir subiendo evidencias/audios.';

  const iniciarEdicion = () => {
    if (!ultimoEntry) return;
    setEditandoId(ultimoEntry.id);
    setTexto(ultimoEntry.observacion ?? '');
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    setTexto('');
  };

  const handleSubmit = async () => {
    const trimmed = texto.trim();
    if (!trimmed || inputDisabled) return;
    setEnviando(true);
    try {
      if (modoEdicion) {
        if (!onEditReply) return;
        await onEditReply(trimmed);
        setEditandoId(null);
      } else {
        if (!onReply) return;
        await onReply(trimmed);
      }
      setTexto('');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className={`cc-chat flex flex-col rounded-lg border border-gray-200 p-2.5 ${className}`}>
      <div className="cc-chat__header flex items-center justify-between mb-2">
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
          <History className="w-3 h-3" /> Histórico
        </span>
        <span data-testid="cc-estado-chip" className={`text-xs font-semibold px-3 py-1 rounded-full ${CHIP_ESTADO_CONVERSACION[estado]}`}>
          {labelChipEstadoConversacion(estado, actorActual)}
        </span>
      </div>
      <div className="cc-chat__scroll flex-1 overflow-y-auto" ref={scrollRef}>
        {orden.length === 0 && <p className="text-sm text-gray-500 text-center py-4">{emptyLabel}</p>}
        {orden.map((entry) => (
          <Fragment key={entry.id}>
            {(entry.estado === 'RECHAZADO' || entry.estado === 'DEVUELTA') && (
              // Marca el momento del rechazo: separa visualmente cada ronda de corrección, para ver
              // desde dónde arranca cada rechazo tanto en la vista del asesor como en la del admin.
              <div className="cc-rechazo-divider flex items-center gap-2 my-3" data-testid="cc-rechazo-divider">
                <span className="flex-1 h-px bg-red-200" />
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-500">
                  <XCircle className="w-3 h-3" /> Rechazo
                </span>
                <span className="flex-1 h-px bg-red-200" />
              </div>
            )}
            <MessageBubble
              entry={entry}
              actorActual={actorActual}
              editable={puedeEditarUltimo && entry.id === ultimoEntry?.id}
              onEditar={iniciarEdicion}
              onEliminarReemplazo={actorActual === 'asesor' || showSecretPanel ? onEliminarReemplazo : undefined}
              showSecretPanel={showSecretPanel}
            />
          </Fragment>
        ))}
      </div>
      {mostrarContador && !modoEdicion && (
        <p
          data-testid="cc-comentarios-restantes"
          className={`text-xs mt-2 ${comentariosRestantes && comentariosRestantes > 0 ? 'text-gray-500' : 'text-amber-600'}`}
        >
          {contadorMensaje}
        </p>
      )}
      {(canReply || modoEdicion) && (
        <div className="cc-chat__reply mt-2">
          <Textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            disabled={inputDisabled}
            placeholder={modoEdicion ? 'Edita tu respuesta...' : 'Escribe tu respuesta...'}
            rows={2}
          />
          <div className="flex justify-end items-center gap-2 mt-1">
            {modoEdicion && (
              <button
                type="button"
                onClick={cancelarEdicion}
                disabled={enviando}
                className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                Cancelar
              </button>
            )}
            <Button type="button" size="sm" disabled={inputDisabled || !texto.trim()} onClick={handleSubmit} isLoading={enviando}>
              {modoEdicion ? 'Guardar' : 'Enviar'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
